import React from 'react';
import { Alert, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { strategyService } from '@/services/strategy.service';

const { Text } = Typography;

/** Soft-fail Universal Strategy Context strip for executive OI screens. */
export function StrategyContextStrip({ compact }: { compact?: boolean }) {
  const q = useQuery({
    queryKey: ['esbi-context-strip'],
    queryFn: () => strategyService.getContext(),
    staleTime: 60_000,
    retry: false,
  });

  if (q.isError || !q.data) {
    return null;
  }

  const data = q.data as any;
  if (data.degraded) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="Strategy context degraded"
        description="ESBI kill switch or unavailable — advisory context hidden."
      />
    );
  }

  const objectives = (data.objectives as any[]) || [];
  const risks = (data.risks as any[]) || [];
  const investments = (data.investments as any[]) || [];

  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <Space wrap size={[8, 4]}>
          <Text strong>Strategy context</Text>
          <Link to="/owner-intelligence/enterprise-strategy">Open hub</Link>
          {!compact ? (
            <>
              <Tag>{objectives.length} objectives</Tag>
              <Tag color="orange">{risks.length} risks</Tag>
              <Tag color="blue">{investments.length} investments</Tag>
            </>
          ) : null}
        </Space>
      }
      description={
        compact ? null : (
          <Space wrap>
            {objectives.slice(0, 3).map((o) => (
              <Tag key={String(o.id)}>{String(o.title).slice(0, 40)}</Tag>
            ))}
            {risks[0] ? (
              <Text type="secondary">Top risk: {String(risks[0].title)}</Text>
            ) : null}
          </Space>
        )
      }
    />
  );
}

export default StrategyContextStrip;
